/**
 * # dxfBridge.entityConversion
 *
 * Forward-direction entity conversion: `DrawingEntity` → `DxfSupportedEntity`.
 *
 * This module owns the geometry-side of the bridge for the forward direction
 * (writing DXF) plus the shared primitive converters (point / vertex / hatch
 * edge) that both directions use. The reverse-direction converter
 * `toDrawingEntity` lives in `dxfBridge.drawingToDxf.ts`.
 *
 * Split from the monolithic `dxfBridge.ts` to respect the CLAUDE.md 2000-line
 * guardrail. All public entry points remain re-exported from `dxfBridge.ts`
 * so consumers don't need to update their imports.
 *
 * ## Primitive converters (bi-directional)
 *
 *   - toDxfPoint / toDrawingPoint          (2D)
 *   - toDxfPoint3D / toDrawingPoint3D      (3D)
 *   - toDxfVertex / toDrawingVertex        (polyline vertex with bulge/width)
 *   - toDxfHatchEdge / toDrawingHatchEdge  (line / arc / elliptic-arc
 *                                            boundary edges)
 *
 * ## XData plumbing
 *
 *   - buildArrayXDataEntry    — serialize array-group membership for writers
 *   - parseArrayXData         — deserialize array-group membership for
 *                               reverse-direction entity conversion
 *
 * Both XData helpers live here because they're a single cluster — keeping
 * `buildArrayXDataEntry` next to `parseArrayXData` avoids the typical
 * split-file maintenance trap where bump-the-writer-but-forget-the-reader
 * goes unnoticed in review.
 */

import {
  type DxfAcadTableEntity,
  type DxfArcEntity,
  type DxfAttdefEntity,
  type DxfCommonEntityProps,
  type DxfCircleEntity,
  type DxfDimensionEntity,
  type DxfHatchBoundaryEdge,
  type DxfHatchBoundaryPath,
  type DxfHatchEntity,
  type DxfInsertEntity,
  type DxfLineEntity,
  type DxfLwPolylineEntity,
  type DxfLwPolylineVertex,
  type DxfMLeaderEntity,
  type DxfMTextEntity,
  type DxfOle2FrameEntity,
  type DxfPoint3D,
  type DxfPolylineEntity,
  type DxfVertexEntity,
  type DxfPointEntity,
  type DxfRevcloudEntity,
  type DxfWipeoutEntity,
  type DxfSupportedEntity,
  type DxfTextEntity,
  type DxfViewportEntity,
  type DxfXDataEntry,
  type DxfGroup,
  DIM_VAR_TABLE,
  dimensionSubtypeSubclassMarkers,
  DIM_BLOCK_EXCLUSIVE,
} from "@dxf-ai-editor/dxf-io";

import { coerceColor, colorToDxf } from "./color";
import { buildDimensionTextMirroredXData } from "./dimensionTextMirroredXData";
// round-4 F1 + round-6 F1/3/4 — the ATTRIB export encoder + its placement
// resolver live in the sibling file (entityConversion is pinned at the 2000-line
// guardrail). Re-exported below to preserve the barrel surface.
import { drawingAttribToDxfAttrib } from "./dxfBridge.attribExportPosition";
export { drawingAttribToDxfAttrib } from "./dxfBridge.attribExportPosition";
// SL-18 — the ARRAYRECT model→DXF decision (MInsert grid vs associative
// block-ref) + geometry builder live in the sibling file so this God-file's
// growth stays ~0.
import {
  buildArrayRectDxfEntity,
  resolveSourceBlockName,
} from "./dxfBridge.arrayMinsert";
import { drawingEllipseToDxfEllipse } from "./dxfBridge.ellipse";
import { drawingSplineToDxfSpline } from "./dxfBridge.spline";
// S-MLEADER-CONTEXT-STRUCTURAL (sub-slice D) — the MULTILEADER CONTEXT_DATA +
// leader-hierarchy model→transport converters live in the sibling file
// (entityConversion is pinned at the 2000-line guardrail; same
// sibling-encoder precedent as attribExportPosition/ellipse/spline above).
// One-directional import (this file → the sibling) — the sibling does NOT
// import back from here (it has its own tiny local `toDxfPoint3D` twin
// specifically to avoid closing an import cycle; see its own header).
import {
  buildAuthoredMLeaderContext,
  modelMLeaderContextToDxf,
  modelMLeaderLeaderRootsToDxf,
  modelMLeaderSecondaryContextsToDxf,
} from "./dxfBridge.mleaderContextConversion";
import { writeViewportOverrides, scaleFromViewHeightRatio } from "./viewportOverrides";
import { maskOrphanNonRectClipBit } from "./viewportGroup90ClipBit";
import {
  resolveFrozenLayerHandlesFromNames,
  mergeFrozenLayerNamesFromHandles,
} from "./viewportFrozenLayers";

import {
  type AttdefEntity,
  type BlockDefinition,
  type DimensionStyleOverrides,
  type DrawingEntity,
  type DrawingPoint,
  type DrawingPoint3D,
  type DrawingSpace,
  type HatchBoundaryEdge,
  type HatchBoundaryPath,
  type PolylineVertex,
  type ViewportEntity,
} from "./model";

export function toDxfPoint(point: DrawingPoint) {
  return {
    x: point.x,
    y: point.y,
    z: 0
  };
}

export function toDrawingPoint(point: { x: number; y: number }): DrawingPoint {
  return {
    x: point.x,
    y: point.y
  };
}

/**
 * Valid DXF group-370 lineweight codes. DXF stores lineweight as an INTEGER
 * code in 1/100 mm drawn from this FIXED enum (AutoCAD `LWEIGHT` list) plus
 * the symbolic codes -2 (ByBlock) / -3 (Default). ByLayer is expressed by
 * OMITTING group 370 (this helper returns null for it), not by emitting -1. A
 * fractional / off-enum value (e.g. 0.25 from the app's mm-decimal authoring,
 * or 24) is NOT a legal group 370 — the parser reads 370 as an integer and a
 * non-enum value corrupts the entity. Source: Autodesk DXF reference (group
 * 370) + AutoCAD lineweight catalogue.
 */
const VALID_DXF_LINEWEIGHT_CODES: readonly number[] = [
  0, 5, 9, 13, 15, 18, 20, 25, 30, 35, 40, 50, 53, 60, 70, 80, 90, 100, 106,
  120, 140, 158, 200, 211,
];

/**
 * Convert a drawing-model lineweight string into a valid DXF group-370 integer
 * code, or `null` when it cannot be resolved (caller omits group 370 — the
 * writer then defaults to ByLayer).
 *
 * The model authors lineweight in two historical forms that BOTH reach the
 * bridge: the app emits mm-decimal strings ("0.25", "0.50", with the symbolic
 * "ByLayer" / "ByBlock" / "Default") via LINEWEIGHT_PRESET_VALUES, while a
 * DXF import re-projects the parsed integer code as a plain string ("25",
 * "50"). This helper handles both by snapping to the NEAREST valid enum code:
 *   - "ByLayer"  → null (writer skips group 370, defaults to ByLayer)
 *   - "ByBlock"  → -2
 *   - "Default"  → -3
 *   - numeric (with optional " mm" suffix / comma decimal):
 *       * an mm-decimal (fractional, or <= 2.11 — the largest enum mm value)
 *         → round(mm * 100) then snap to nearest enum code ("0.25" → 25)
 *       * an already-integer enum code (e.g. "25", "50") → snap to nearest
 *         enum (idempotent — "25" → 25)
 *   - anything else (unparseable, RGB/true-color strings out of this slice's
 *     scope) → null (never emit a bogus 370)
 *
 * Dep-safe by design: this lives in drawing-model and does NOT import the
 * app's LINEWEIGHT_PRESET_VALUES (apps/web is downstream of the bridge); the
 * mm / comma / " mm" parsing mirrors that constant's contract locally.
 */
export function lineweightStringToDxfCode(value: string): number | null {
  // Defensive: many entity literals are cast onto the typed model without a
  // Zod parse, so a runtime `lineweight` may legitimately be absent / non
  // -string (the schema default "ByLayer" only applies on parse). Treat any
  // non-string as "no resolvable code" — matches the prior NaN-tolerant guard.
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "ByLayer") return null;
  if (trimmed === "ByBlock") return -2;
  if (trimmed === "Default") return -3;
  // Normalise the app's authored forms: optional " mm" suffix, comma decimal.
  const numeric = trimmed.replace(/\s*mm$/i, "").replace(",", ".");
  const parsed = Number(numeric);
  if (!Number.isFinite(parsed)) return null;
  // The three SYMBOLIC group-370 codes ALSO arrive as plain numeric strings,
  // because a DXF import re-projects the parsed integer verbatim
  // (`dxfBridge.drawingToDxf.ts`: `String(entity.common.lineweight)`), so a
  // file carrying `370 -1` / `-2` / `-3` yields the model strings "-1" / "-2"
  // / "-3" — NOT the "ByLayer" / "ByBlock" / "Default" spellings handled
  // above, which are the app's authoring forms. They must be resolved BEFORE
  // the mm-scaling heuristic below: -2 sits inside its `<= 2.11` mm window,
  // so it would be read as -2.00 mm, scale to -200 and snap to the nearest
  // enum code 0 — silently turning every imported ByBlock lineweight into a
  // hard 0.00 mm on the first open-and-save (observed on the `*T<N>` table
  // graphics blocks of `apps/web/public/abi1-test.dxf`, whose grid LINEs all
  // carry `370 -2`). -1 is ByLayer, which DXF expresses by OMITTING group
  // 370, hence null rather than -1.
  // Source: Autodesk DXF reference group 370; ezdxf 1.4.4
  // `const.LINEWEIGHT_BYLAYER = -1 / _BYBLOCK = -2 / _DEFAULT = -3`.
  if (parsed === -1) return null;
  if (parsed === -2 || parsed === -3) return parsed;
  // No other negative value exists in the group-370 enum. Snapping one to the
  // nearest positive code would fabricate a lineweight the file never had, so
  // it is unmappable — the caller omits 370 and the entity stays ByLayer.
  if (parsed < 0) return null;
  // Decide the unit: a fractional value, or any value within the enum's mm
  // range (<= 2.11), is an mm-decimal and scales by 100; a larger integer is
  // already a raw 1/100-mm code.
  const hasFraction = !Number.isInteger(parsed);
  const code = hasFraction || parsed <= 2.11 ? Math.round(parsed * 100) : parsed;
  // Snap to the nearest valid enum code so both "0.25"→25 and "25"→25 resolve.
  let best: number | null = null;
  let bestDelta = Infinity;
  for (const candidate of VALID_DXF_LINEWEIGHT_CODES) {
    const delta = Math.abs(candidate - code);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = candidate;
    }
  }
  return best;
}

/**
 * MLEADER leader-lineweight string↔enum mapping for DXF group 171 (Codex #258
 * F3). Lives at the BRIDGE boundary — the same layer as
 * `lineweightStringToDxfCode` — because that is the established home for the
 * mm-string↔DXF-enum mapping (apps/web + the typed model author the mm-string
 * form; the dxf-io transport carries the raw DXF enum). dxf-io is UPSTREAM of
 * drawing-model and cannot import this helper, so the conversion must happen
 * here, before the value is placed on / after it is read from the dxf-io
 * `geometry.leaderLineWeight` transport field.
 *
 * Unlike the common group-370 mapping, MLEADER body group 171 ALWAYS carries an
 * explicit enum (ezdxf `acdb_mleader.leader_lineweight`, default BYBLOCK = -2),
 * so ByLayer is encoded as the explicit enum -1 (NOT omission). BYBLOCK = -2,
 * Default = -3; an mm value snaps to the nearest valid enum (reusing
 * `lineweightStringToDxfCode`).
 *
 * `modelLeaderLineWeightToDxf171` returns the integer enum for emit; null when
 * the model string is the BYBLOCK default (caller omits the transport field, so
 * the writer skips 171 and the parser re-defaults to ByBlock — preserving the
 * existing compact-default behaviour and the F1 `g.leaderLineWeight === "-2"`
 * round-trip contract).
 */
export function modelLeaderLineWeightToDxf171(value: string): number | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  // ByBlock is the MLEADER default — omit the transport field (compact default).
  if (trimmed === "" || trimmed === "ByBlock") return null;
  if (trimmed === "ByLayer") return -1;
  if (trimmed === "Default") return -3;
  // Some imports re-project the raw enum as a plain string ("-1"/"-2"/"-3");
  // pass those symbolic enums through unchanged so re-export is idempotent.
  if (trimmed === "-1" || trimmed === "-2" || trimmed === "-3") {
    return Number(trimmed);
  }
  // mm-decimal or already-enum mm code → nearest valid enum (e.g. "0.25" → 25).
  return lineweightStringToDxfCode(trimmed);
}

/**
 * Inverse of {@link modelLeaderLineWeightToDxf171}: map a DXF group-171 enum
 * (carried on the transport as a string, e.g. "-2" / "25") back to the
 * drawing-model lineweight string ("ByBlock" / "ByLayer" / "Default" / mm
 * decimal). Mirrors how the common lineweight enum is re-projected to the model
 * string on import.
 *
 *   -1 → "ByLayer"   -2 → "ByBlock"   -3 → "Default"
 *   0..211 enum code → mm-decimal string ("25" → "0.25")
 *
 * Returns "ByBlock" for an unparseable / absent value (the MLEADER default).
 */
export function dxf171ToModelLeaderLineWeight(value: string | undefined): string {
  if (value === undefined) return "ByBlock";
  const trimmed = value.trim();
  if (trimmed === "" ) return "ByBlock";
  const code = Number(trimmed);
  if (!Number.isFinite(code)) return "ByBlock";
  if (code === -1) return "ByLayer";
  if (code === -2) return "ByBlock";
  if (code === -3) return "Default";
  // Positive enum codes are 1/100 mm. Emit the mm-decimal form the model
  // authors ("25" → "0.25"); strip a trailing zero so 0.50 reads "0.5".
  const mm = code / 100;
  return String(mm);
}

export function toDxfCommonProps(space: DrawingSpace): DxfSupportedEntity["common"] {
  if (space.kind === "layout") {
    return {
      space: "paper",
      layoutName: space.name
    };
  }

  return {
    space: "model"
  };
}

/**
 * App ID used to register our XDATA block that stores array group
 * tracking fields (arrayGroupId, arraySourceOfGroup, arrayMode) on
 * every entity participating in an ARRAYRECT / ARRAYPOLAR / ARRAYPATH.
 *
 * AutoCAD requires XDATA app IDs to be uppercase, start with a letter,
 * and be registered through the APPID table in real DXF files. For our
 * round-trip use we parse and emit unconditionally — if a consuming
 * application does not recognise the app ID it silently ignores the
 * XDATA block (per DXF reference), which is the desired behaviour.
 */
export const EDIT_DWG_ARRAY_APPID = "EDIT_DWG_ARRAY_GROUP";

/**
 * Build the XDATA entry that records array group membership for a
 * drawing entity. Returns `null` when the entity carries no array
 * metadata (no groupId) so we can skip emitting empty XDATA blocks.
 *
 * XDATA layout:
 *   1001  EDIT_DWG_ARRAY_GROUP          (app id marker)
 *   1000  <arrayGroupId>                (always present when XDATA emitted)
 *   1002  {                              (begin sub-list — optional marker)
 *   1070  <flag> 0=copy, 1=source
 *   1000  <arrayMode> ("rect" | "polar" | "path") — optional
 *   1002  }                              (end sub-list)
 *
 * The 1002 { ... } enclosure is the AutoCAD-standard way to group
 * related XDATA codes; it keeps the block structurally unambiguous if
 * we add more fields later.
 */
export function buildArrayXDataEntry(entity: {
  arrayGroupId?: string;
  arraySourceOfGroup?: string;
  arrayMode?: string;
}): { appName: string; groups: { code: number; value: string }[] } | null {
  if (!entity.arrayGroupId) return null;
  const groups: { code: number; value: string }[] = [];
  groups.push({ code: 1000, value: entity.arrayGroupId });
  groups.push({ code: 1002, value: "{" });
  const sourceFlag = entity.arraySourceOfGroup === entity.arrayGroupId ? 1 : 0;
  groups.push({ code: 1070, value: String(sourceFlag) });
  if (entity.arrayMode) {
    groups.push({ code: 1000, value: entity.arrayMode });
  }
  groups.push({ code: 1002, value: "}" });
  return { appName: EDIT_DWG_ARRAY_APPID, groups };
}

export type ArrayXDataFields = {
  arrayGroupId?: string;
  arraySourceOfGroup?: string;
  arrayMode?: "rect" | "polar" | "path";
};

/**
 * Parse the EDIT_DWG_ARRAY_GROUP XDATA block on a DXF entity and
 * extract arrayGroupId / arraySourceOfGroup / arrayMode. Returns an
 * empty object when the entity carries no such XDATA block. Tolerates
 * missing sub-list markers and unknown group codes (forward compat).
 */
export function parseArrayXData(entity: DxfSupportedEntity): ArrayXDataFields {
  const xdata = entity.xdata;
  if (!xdata || xdata.length === 0) return {};
  const entry = xdata.find(e => e.appName === EDIT_DWG_ARRAY_APPID);
  if (!entry) return {};

  const out: ArrayXDataFields = {};
  let stringIndex = 0;
  for (const g of entry.groups) {
    if (g.code === 1000) {
      // The first 1000 is the groupId, the second is the arrayMode.
      if (stringIndex === 0) {
        out.arrayGroupId = g.value;
      } else if (stringIndex === 1) {
        const mode = g.value as ArrayXDataFields["arrayMode"];
        if (mode === "rect" || mode === "polar" || mode === "path") {
          out.arrayMode = mode;
        }
      }
      stringIndex += 1;
    } else if (g.code === 1070) {
      // Source flag: 1 → source entity, 0 → copy
      if (g.value.trim() === "1" && out.arrayGroupId) {
        out.arraySourceOfGroup = out.arrayGroupId;
      }
    }
    // Other codes (1002 list markers, future fields) are ignored.
  }
  return out;
}

/**
 * Build an ACAD/DSTYLE XData entry encoding per-entity DIMVAR overrides
 * for a DIMENSION entity. AutoCAD format:
 *
 *   1001 ACAD            (appName — entry.appName)
 *   1000 DSTYLE          (block marker)
 *   1002 {               (open paren)
 *   1070 <dimvar-code>   (e.g. 41 for DIMASZ)
 *   <value-code> <value> (1040 real / 1070 int / 1000 string)
 *   ... (repeating)
 *   1002 }               (close paren)
 *
 * Iterates DIM_VAR_TABLE in declaration order so output is deterministic
 * — keeps round-trip diffs minimal. Returns `null` when there are no
 * overrides so the writer can skip emitting an empty xdata block.
 */
export function buildDimensionStyleOverridesXData(
  overrides: DimensionStyleOverrides | undefined,
): DxfXDataEntry | null {
  if (!overrides) return null;
  // SL-11 #3/#4 — a blockRef DIMVAR emits an authored NAME as a `1000` placeholder (stamp resolves), else the imported `dimblk*Handle` twin as `1005 <handle>`.
  const ov = overrides as Record<string, string | number | boolean | undefined>;
  const importedHandleOf = (def: (typeof DIM_VAR_TABLE)[number]): string | undefined =>
    def.blockRef ? (ov[`${def.key}Handle`] as string | undefined) : undefined;
  const present = DIM_VAR_TABLE.filter(
    (def) => overrides[def.key] !== undefined || importedHandleOf(def) !== undefined,
  );
  if (present.length === 0) return null;

  const groups: DxfGroup[] = [];
  groups.push({ code: 1000, value: "DSTYLE" });
  groups.push({ code: 1002, value: "{" });
  for (const def of present) {
    const value = overrides[def.key];
    const importedHandle = importedHandleOf(def);
    groups.push({ code: 1070, value: String(def.groupCode) });
    if (def.blockRef && value === undefined && importedHandle !== undefined) {
      groups.push({ code: 1005, value: String(importedHandle) });
    } else if (def.blockRef || def.type === "string") {
      groups.push({ code: 1000, value: String(value) });
    } else if (def.type === "boolean") {
      groups.push({ code: 1070, value: value ? "1" : "0" });
    } else if (def.type === "int") {
      groups.push({ code: 1070, value: String(Math.trunc(Number(value))) });
    } else {
      groups.push({ code: 1040, value: String(Number(value)) }); // number → 1040
    }
  }
  groups.push({ code: 1002, value: "}" });
  return { appName: "ACAD", groups };
}

/**
 * Compose the raw DXF group-70 ATTDEF/ATTRIB flag integer from the decomposed
 * `AttdefFlags` booleans (model.ts attdefFlagsSchema). Mirrors the documented
 * AutoCAD bit weights so the emitted 70 re-decodes to the same flag set.
 */
function attdefFlagsToInt(flags: AttdefEntity["flags"]): number {
  return (
    (flags.invisible ? 1 : 0) |
    (flags.constant ? 2 : 0) |
    (flags.verify ? 4 : 0) |
    (flags.preset ? 8 : 0) |
    (flags.lockPosition ? 16 : 0) |
    (flags.multiline ? 32 : 0)
  );
}

/**
 * FIX D (round-9 SG-1) — build a DxfAttdefEntity for a TYPED block-child ATTDEF.
 *
 * `buildBlocksSectionFromDrawingBlocks` previously DROPPED every typed Attdef
 * child (`if (child.type === "Attdef") continue;`) on the premise that its raw
 * counterpart in `block.rawEntityGroups` re-emits it verbatim. That holds for an
 * IMPORTED block (which carries BOTH a typed Attdef and a raw ATTDEF group), but
 * a FROM-SCRATCH / BEDIT block has ONLY the typed Attdef — so the whole ATTDEF +
 * its handle was lost. This encoder lets the typed Attdef emit (with its minted
 * hex handle) on the from-scratch path; the caller de-dups against any raw ATTDEF
 * counterpart so an imported block still emits exactly once (no double-encode).
 *
 * Mirrors `drawingAttribToDxfAttrib`'s common-props / handle plumbing and maps
 * every persisted ATTDEF field (model.ts attdefEntitySchema) to the dxf-io
 * geometry shape (types.ts DxfAttdefEntity) so the writer's ATTDEF branch emits
 * the canonical group set.
 */
export function drawingAttdefToDxfAttdef(
  attdef: AttdefEntity,
  space: DrawingSpace,
  options?: {
    /**
     * FINDING 1 — when true, stamp `common.forceLayerEmit` so the ATTDEF child
     * emits group 8 (layer) on EVERY record, including the default layer "0",
     * consistent with the rest of the conformant from-scratch export (user
     * decision: "write code 8 on every entity"). The block-child loop sets this
     * on the from-scratch path; without it the typed ATTDEF silently omitted
     * group 8 for layer "0". Omitted on the carry-forward / imported round-trip
     * path → byte-identical "omit code 8 when 0".
     */
    forceLayerEmit?: boolean;
  },
): DxfAttdefEntity {
  const common: DxfAttdefEntity["common"] = {
    ...toDxfCommonProps(space),
    layer: attdef.layer,
  };
  if (attdef.handle) common.handle = attdef.handle;
  // BUG 2 (round-11 P2) — project the FULL common-property set (color / linetype /
  // lineweight / transparency / linetypeScale / extrusion + the 330·347·390·360
  // reference handles + visibility / shadowMode), exactly like `toDxfEntity`, via
  // the SHARED `projectCommonEntityProps`. Previously only layer + handle were
  // copied, so a typed ATTDEF with a non-default color / linetype / etc silently
  // reverted to defaults on export. `projectCommonEntityProps` re-asserts a
  // non-default `layer` (the explicit set above already wins for "0"-vs-non-"0").
  projectCommonEntityProps(common, attdef);
  // FINDING 1 — stamp the conformant `forceLayerEmit` AFTER `projectCommonEntityProps`
  // (mirrors `toDxfEntity`'s order) so the ATTDEF emits group 8 like every other
  // conformant entity, including for the default layer "0".
  if (options?.forceLayerEmit) common.forceLayerEmit = true;
  const flagsInt = attdefFlagsToInt(attdef.flags);
  const geometry: DxfAttdefEntity["geometry"] = {
    insertionPoint: toDxfPoint(attdef.insertionPoint),
    height: attdef.height,
    defaultValue: attdef.defaultValue,
    tag: attdef.tag,
    prompt: attdef.prompt,
    flags: flagsInt,
    flagBits: {
      invisible: attdef.flags.invisible,
      constant: attdef.flags.constant,
      verify: attdef.flags.verify,
      preset: attdef.flags.preset,
      lockPosition: attdef.flags.lockPosition,
      multiline: attdef.flags.multiline,
    },
    rotation: attdef.rotation,
    styleName: attdef.styleName,
    widthFactor: attdef.widthFactor,
    obliqueAngle: attdef.obliqueAngle,
    generationFlags: attdef.generationFlags,
    hJustification: attdef.hJustification,
    vJustification: attdef.vJustification,
    fillBoxScale: attdef.fillBoxScale,
    ...(attdef.alignmentPoint
      ? { alignmentPoint: toDxfPoint(attdef.alignmentPoint) }
      : {}),
    ...(attdef.flags.lockPosition ? { lockPosition: true } : {}),
  };
  // BUG 4 (round-13) — project the ATTDEF MTEXT-style BACKGROUND-FILL fields so a
  // from-scratch / BEDIT ATTDEF authored with `backgroundFill` enabled emits the
  // canonical group set (90 bg-fill flag, 63 bg ACI colour, 45 bg scale factor —
  // matching the writer's ATTDEF branch + the MTEXT/ATTRIB encoder projection).
  // Previously the encoder set ONLY `fillBoxScale`, but the writer emits group 45
  // (and 63) ONLY inside the `backgroundFill !== 0` guard, so without group 90 the
  // ENTIRE bg-fill group set was silently dropped on export. The writer emits group
  // 90 gated `!== 0`, so a disabled (false) fill projects nothing. The model stores
  // `backgroundFill` as a boolean (DXF 90 != 0) — emit 1 when enabled. The optional
  // `backgroundColor` string carries the ACI index (group 63); parse it to an
  // integer when numeric, else omit (the writer falls back to no explicit colour).
  if (attdef.backgroundFill) {
    geometry.backgroundFill = 1;
    if (attdef.backgroundColor !== undefined) {
      const aci = Number.parseInt(attdef.backgroundColor, 10);
      if (Number.isFinite(aci) && aci !== 0) {
        geometry.backgroundFillColorACI = aci;
      }
    }
  }
  return {
    kind: "supported-entity",
    type: "ATTDEF",
    common,
    subclassMarkers: [],
    extraGroups: [],
    geometry,
  };
}

export function toDxfPoint3D(point: { x: number; y: number; z?: number }): DxfPoint3D {
  // Default z to 0 (matches the model's z-defaulting semantics). Reducers,
  // tests and AI command builders frequently construct point literals as 2D
  // {x,y} and cast them onto the typed model without going through Zod parse,
  // so a runtime z may legitimately be absent (LINE start/end, POINT
  // location use the optional-z schema) — emit 0 rather than a DXF group
  // 30/31 carrying `undefined` (DEFUSE S1b). DrawingPoint3D (z required) is
  // assignable to this widened param, so existing callers stay valid.
  return { x: point.x, y: point.y, z: point.z ?? 0 };
}

export function toDrawingPoint3D(point: DxfPoint3D): DrawingPoint3D {
  return { x: point.x, y: point.y, z: point.z };
}

export function toDrawingVertex(v: DxfLwPolylineVertex): PolylineVertex {
  const out: PolylineVertex = {
    x: v.x,
    y: v.y,
    bulge: v.bulge ?? 0,
    startWidth: v.startWidth ?? 0,
    endWidth: v.endWidth ?? 0
  };
  // Preserve the persistent vertex identifier across import.
  // See DxfLwPolylineVertex.vertexId for semantics.
  if (v.vertexId !== undefined) out.vertexId = v.vertexId;
  return out;
}

export function toDxfVertex(v: PolylineVertex): DxfLwPolylineVertex {
  const result: DxfLwPolylineVertex = { x: v.x, y: v.y };
  if (v.bulge) result.bulge = v.bulge;
  if (v.startWidth) result.startWidth = v.startWidth;
  if (v.endWidth) result.endWidth = v.endWidth;
  if (v.vertexId !== undefined) result.vertexId = v.vertexId;
  return result;
}

export function toDxfHatchEdge(edge: HatchBoundaryEdge): DxfHatchBoundaryEdge {
  if (edge.edgeType === "line") {
    return {
      edgeType: "line",
      start: toDxfPoint(edge.start),
      end: toDxfPoint(edge.end)
    };
  }
  if (edge.edgeType === "arc") {
    return {
      edgeType: "arc",
      center: toDxfPoint(edge.center),
      radius: edge.radius,
      startAngle: edge.startAngle,
      endAngle: edge.endAngle,
      counterclockwise: edge.counterclockwise ?? false
    };
  }
  return {
    edgeType: "ellipticArc",
    center: toDxfPoint(edge.center),
    majorAxisEndpoint: toDxfPoint(edge.majorAxisEnd),
    minorAxisRatio: edge.minorRatio,
    startAngle: edge.startAngle,
    endAngle: edge.endAngle,
    counterclockwise: edge.counterclockwise ?? false
  };
}

export function toDrawingHatchEdge(edge: DxfHatchBoundaryEdge): HatchBoundaryEdge {
  if (edge.edgeType === "line") {
    return {
      edgeType: "line",
      start: toDrawingPoint(edge.start),
      end: toDrawingPoint(edge.end)
    };
  }
  if (edge.edgeType === "arc") {
    return {
      edgeType: "arc",
      center: toDrawingPoint(edge.center),
      radius: edge.radius,
      startAngle: edge.startAngle,
      endAngle: edge.endAngle,
      counterclockwise: edge.counterclockwise
    };
  }
  return {
    edgeType: "ellipticArc",
    center: toDrawingPoint(edge.center),
    majorAxisEnd: toDrawingPoint(edge.majorAxisEndpoint),
    minorRatio: edge.minorAxisRatio,
    startAngle: edge.startAngle,
    endAngle: edge.endAngle,
    counterclockwise: edge.counterclockwise
  };
}

/**
 * The structural shape `projectCommonEntityProps` reads — the per-entity GENERAL
 * properties + common reference handles on `drawingEntityBaseSchema`. Both the
 * `DrawingEntity` discriminated union AND `AttdefEntity` (which is NOT in that
 * union but extends the SAME base schema) satisfy it, so the ONE projection is
 * shared between `toDxfEntity` and `drawingAttdefToDxfAttdef` (BUG 2 — the ATTDEF
 * encoder previously copied only layer+handle, dropping color/linetype/lineweight/
 * transparency/linetypeScale/extrusion + the 347/390/360 reference handles).
 */
type CommonPropsSource = DrawingEntity | AttdefEntity;

/**
 * Project the per-entity GENERAL properties + common reference handles from the
 * typed model onto the transport `common` (DXF groups 8 / 62·420·430 / 6 / 370 /
 * 440 / 39 / 330 / 347 / 390 / 360 / 60 / 284 / 210·220·230 / 48). Symmetric to
 * the import mapping in dxfBridge.drawingToDxf.ts (`toDrawingEntity` `base`).
 * Each value is emitted only when it differs from the AutoCAD default so the
 * writer output stays compact and still round-trips. SINGLE SOURCE OF TRUTH:
 * called by BOTH `toDxfEntity` and `drawingAttdefToDxfAttdef`, so a typed ATTDEF
 * with a non-default color / linetype / etc round-trips identically to any other
 * primitive (BUG 2). Mutates `common` in place (the `layer`/`handle` keys are set
 * by the caller before this runs — only a NON-default layer overrides "0" here).
 */
export function projectCommonEntityProps(
  common: DxfSupportedEntity["common"],
  entity: CommonPropsSource,
): void {
  // Per-entity general properties (layer / color / linetype / lineweight /
  // transparency / thickness) — S-COMMON-EXPORT data-loss fix.
  if (entity.layer && entity.layer !== "0") {
    common.layer = entity.layer;
  }
  // EPIC-COLOR-UNION S1 — project the typed `EntityColor` union back to the DXF
  // group codes (62 / 420 / 430). `colorToDxf` returns {} for ByLayer so the
  // writer omits group 62 (byte-identical with the prior ByLayer path); ByBlock
  // → 62=0; index → 62=aci; rgb → 420 (+62/430 if carried); bookColor → 430.
  // `coerceColor` defends against raw entity literals (tests / command builders)
  // that bypassed the schema and carry a legacy-string or absent `color` — it is
  // idempotent on an already-typed union.
  Object.assign(common, colorToDxf(coerceColor(entity.color)));
  if (entity.linetype && entity.linetype !== "ByLayer") {
    common.linetype = entity.linetype;
  }
  if (entity.lineweight !== "ByLayer") {
    // DXF group 370 is an INTEGER lineweight code in 1/100 mm from a FIXED
    // enum (plus -2 ByBlock / -3 Default). The app authors mm-decimal strings
    // ("0.25"); a DXF import re-projects the integer code as a string ("25").
    // The helper resolves both to a valid code (snap-to-nearest-enum) and
    // returns null for anything it cannot map — never emit a bogus 370.
    const lwCode = lineweightStringToDxfCode(entity.lineweight);
    if (lwCode !== null) {
      common.lineweight = lwCode;
    }
  }
  if (entity.transparency !== "0") {
    const t = Number(entity.transparency);
    if (Number.isFinite(t)) {
      common.transparency = t;
    }
  }
  // `thickness` is a per-entity field (Line/Point/Circle/Arc/Ellipse), not on
  // DrawingEntityBase — narrow before reading. (ATTDEF carries no thickness.)
  if (
    "thickness" in entity &&
    typeof entity.thickness === "number" &&
    entity.thickness !== 0
  ) {
    common.thickness = entity.thickness;
  }

  // DXF common reference handles + flags (S-COMMON-HANDLES). Emit only when
  // present so writer output stays compact; shadowMode 0 is the AutoCAD default
  // and is skipped (the writer also omits it).
  if (entity.ownerHandle) {
    common.ownerHandle = entity.ownerHandle;
  }
  if (entity.materialHandle) {
    common.materialHandle = entity.materialHandle;
  }
  if (entity.plotStyleHandle) {
    common.plotStyleHandle = entity.plotStyleHandle;
  }
  // Entity extension-dictionary 360 handle (Batch-8). Set before any INSERT
  // override so the INSERT representation-data path can still override it.
  if (entity.extensionDictionaryHandle) {
    common.extensionDictionaryHandle = entity.extensionDictionaryHandle;
  }
  if (entity.visibility !== undefined) {
    common.visibility = entity.visibility;
  }
  if (entity.shadowMode !== undefined && entity.shadowMode !== 0) {
    common.shadowMode = entity.shadowMode;
  }

  // DXF group 210/220/230 — OCS extrusion normal (S-EXTRUSION-STORE). Carry a
  // NON-default extrusion from the typed model back onto the transport common so
  // the writer emits 210/220/230 (the COMMON group-writer skips an absent /
  // identity (0,0,1) normal — keeping output compact). `extrusion` is a
  // per-entity field (not on DrawingEntityBase) — narrow with `"extrusion" in`.
  //
  // Block/INSERT, Ellipse, Text and Hatch are EXCLUDED from this generic narrow:
  //   - Block/INSERT ships its extrusion inside its own `geometry.extrusion`
  //     block (writer emits 210/220/230 from there) — setting common.extrusion
  //     too would DOUBLE-emit the codes.
  //   - Ellipse sets `common.extrusion` in its dedicated helper
  //     (drawingEllipseToDxfEllipse).
  //   - Text does not export extrusion via this path (left unchanged).
  //   - Hatch is SINGLE-CHANNEL (S-HATCH-EXTRUSION): its OCS normal lives ONLY on
  //     `geometry.extrusion`, set in the HATCH export case and read by
  //     toDrawingEntity's `g.extrusion`. Routing it through common here would
  //     orphan it on import (which never reads common.extrusion for HATCH).
  // The OCS entities that reach this projection (Line/Point/Circle/Arc/LWPOLYLINE
  // + ATTDEF) carry their extrusion through the common channel here. MText (Codex
  // #342 F3) is NOT excluded: a mirrored MTEXT's (0,0,−1) handedness rides this
  // generic common channel. ATTDEF (a TYPED from-scratch attdef has NO raw
  // counterpart) likewise emits its 210/220/230 here — passing the type guard
  // (not Block/Ellipse/Text/Hatch) and carrying an optional `extrusion` field.
  // SL-15 round-7 @codex F2 — a NONPLANAR spline NEVER emits 210 (symmetric EXPORT
  // gate to round-6 F3's IMPORT gate): an in-memory `planar:false` SplineEntity
  // with a non-default `extrusion` must not leak 210 (ezdxf: plane vector iff planar).
  const isNonplanarSpline = entity.type === "Spline" && !entity.planar;
  if (
    entity.type !== "Block" &&
    entity.type !== "Ellipse" &&
    entity.type !== "Text" &&
    entity.type !== "Hatch" &&
    !isNonplanarSpline &&
    "extrusion" in entity &&
    entity.extrusion &&
    !(
      entity.extrusion.x === 0 &&
      entity.extrusion.y === 0 &&
      entity.extrusion.z === 1
    )
  ) {
    common.extrusion = toDxfPoint3D(entity.extrusion);
  }

  // Per-entity linetype scale (PDF-6.1). Carry a non-default value back onto the
  // transport common so the writer emits DXF group 48 (it skips the code when the
  // value is 1 — the AutoCAD default — keeping output compact).
  const entityLinetypeScale = entity.linetypeScale;
  if (
    typeof entityLinetypeScale === "number" &&
    Number.isFinite(entityLinetypeScale) &&
    entityLinetypeScale !== 1
  ) {
    common.linetypeScale = entityLinetypeScale;
  }
}

export function toDxfEntity(
  entity: DrawingEntity,
  space: DrawingSpace,
  options?: {
    /**
     * FIX — when true, stamp `common.forceLayerEmit` so the writer emits group 8
     * (layer) on EVERY entity, including the default layer "0". Set by the
     * conformant from-scratch export (user decision: "write code 8 on every
     * entity"). Omitted on the carry-forward / imported round-trip path, which
     * preserves the byte-identical "omit code 8 when 0" behaviour.
     */
    forceLayerEmit?: boolean;
    blocks?: Record<string, BlockDefinition>; // round-4 F1 — threaded to drawingAttribToDxfAttrib.
    /**
     * EXPORT-VP-FROZEN — the document's LAYER table (name + record handle),
     * threaded so the VIEWPORT branch can resolve the semantic
     * `frozenLayers` NAMES → native group-331 LAYER handles
     * (`resolveFrozenLayerHandlesFromNames`). Omitted by unit callers that do
     * not carry a layer table: the resolver then preserves any verbatim
     * `frozenLayerHandles` unchanged (pre-unification behaviour).
     */
    layers?: readonly { name: string; handle: string }[];
  },
): DxfSupportedEntity {
  const common: DxfSupportedEntity["common"] = {
    ...toDxfCommonProps(space),
    handle: entity.id
  };

  // Symmetric to the import mapping in dxfBridge.drawingToDxf.ts — project the
  // FULL per-entity general-property + common-reference-handle set (SINGLE SOURCE
  // OF TRUTH, shared with the ATTDEF encoder; BUG 2).
  projectCommonEntityProps(common, entity);
  // FIX — conformant from-scratch export: force an explicit `8/<layer>` on every
  // entity. The flag rides on the transport `common` so the dxf-io writer's
  // group-8 emitter can honour it without re-deriving the conformant gate.
  if (options?.forceLayerEmit) {
    common.forceLayerEmit = true;
  }

  const arrayXdata = buildArrayXDataEntry(entity);

  const base = {
    kind: "supported-entity" as const,
    common,
    subclassMarkers: [] as string[],
    // Entity-level unknown / vendor DXF group passthrough (S-COMMON-XDATA).
    // Carry the typed model's raw groups (structurally identical to the
    // dxf-io DxfGroup shape) back onto the transport entity so the writer
    // re-emits them verbatim — symmetric to the import mapping in
    // dxfBridge.drawingToDxf.ts. Empty array when the entity had none.
    // Separate channel from `xdata` (array-XData) below — do not conflate.
    //
    // MLEADER EXCLUSION: the dxf-io parser decodes the CONTEXT_DATA / LEADER /
    // LEADER_LINE marker groups (300/302/303 + nested 10/20/30/11/21/31 …) into
    // geometry.leaderRoots (and annotativeContexts / blockAttributeReferences)
    // WHILE ALSO leaving those same markers in draft.extraGroups — they have no
    // MLEADER group-reader and fall through to the extraGroups passthrough. The
    // MLEADER writer re-emits the decoded leaderRoots structure as a fresh
    // CONTEXT_DATA block, so carrying the raw markers too would DOUBLE-emit /
    // corrupt the output. MLEADER's full structure round-trips via leaderRoots,
    // so we drop its extraGroups wholesale (a per-code filter is fragile). This
    // is the ONLY entity type with this decoded-and-also-in-extraGroups overlap:
    // every other finalizer pushes to extraGroups ONLY on the not-consumed
    // passthrough path. Symmetric to the import-side exclusion in
    // dxfBridge.drawingToDxf.ts.
    extraGroups: (entity.type === "Multileader"
      ? []
      : entity.extraGroups ?? []) as { code: number; value: string }[],
    ...(arrayXdata ? { xdata: [arrayXdata] } : {})
  };

  if (entity.type === "Line") {
    const e: DxfLineEntity = {
      ...base,
      type: "LINE",
      geometry: {
        // Emit 3D endpoints (DXF group 30/31) so z survives export (DEFUSE S1b).
        start: toDxfPoint3D(entity.start),
        end: toDxfPoint3D(entity.end)
      }
    };
    return e;
  }

  if (entity.type === "Point") {
    const geom: DxfPointEntity["geometry"] = {
      // Emit 3D location (DXF group 30) so z survives export (DEFUSE S1b).
      location: toDxfPoint3D(entity.location)
    };
    if (entity.rotation !== 0) {
      geom.rotation = entity.rotation;
    }
    const e: DxfPointEntity = {
      ...base,
      type: "POINT",
      geometry: geom
    };
    return e;
  }

  if (entity.type === "Circle") {
    const e: DxfCircleEntity = {
      ...base,
      type: "CIRCLE",
      geometry: {
        // Emit 3D center (DXF group 30, z ?? 0) — DEFUSE S-ARC-CIRCLE-CENTER-Z.
        center: toDxfPoint3D(entity.center),
        radius: entity.radius
      }
    };
    return e;
  }

  if (entity.type === "Arc") {
    const e: DxfArcEntity = {
      ...base,
      type: "ARC",
      geometry: {
        // Emit 3D center (DXF group 30, z ?? 0) — DEFUSE S-ARC-CIRCLE-CENTER-Z.
        center: toDxfPoint3D(entity.center),
        radius: entity.radius,
        startAngle: entity.startAngle,
        endAngle: entity.endAngle
      }
    };
    return e;
  }

  if (entity.type === "Ellipse") {
    return drawingEllipseToDxfEllipse(entity, common, base);
  }

  if (entity.type === "Spline") {
    return drawingSplineToDxfSpline(entity, common, base);
  }

  if (entity.type === "LWPOLYLINE") {
    const geom: DxfLwPolylineEntity["geometry"] = {
      vertices: entity.vertices.map(toDxfVertex),
      closed: entity.closed,
      vertexCount: entity.vertices.length
    };
    // DXF group 70 bit 128 (S-LWPOLYLINE) — only set when true so the
    // writer emits a compact 70 for plain polylines.
    if (entity.plinegen) geom.plinegen = true;
    if (entity.constantWidth) geom.constantWidth = entity.constantWidth;
    if (entity.elevation) geom.elevation = entity.elevation;
    return { ...base, type: "LWPOLYLINE", geometry: geom } satisfies DxfLwPolylineEntity;
  }

  if (entity.type === "Text") {
    const geom: DxfTextEntity["geometry"] = {
      insertionPoint: toDxfPoint(entity.insertionPoint),
      height: entity.height,
      contents: entity.contents
    };
    if (entity.alignmentPoint) geom.alignmentPoint = toDxfPoint(entity.alignmentPoint);
    if (entity.rotation) geom.rotation = entity.rotation;
    if (entity.styleName) geom.styleName = entity.styleName;
    if (entity.widthFactor !== 1) geom.widthFactor = entity.widthFactor;
    if (entity.obliqueAngle) geom.obliqueAngle = entity.obliqueAngle;
    if (entity.hJustification) geom.hJustification = entity.hJustification;
    if (entity.vJustification) geom.vJustification = entity.vJustification;
    if (entity.generationFlags) geom.generationFlags = entity.generationFlags;
    return { ...base, type: "TEXT", geometry: geom } satisfies DxfTextEntity;
  }

  if (entity.type === "MText") {
    const geom: DxfMTextEntity["geometry"] = {
      insertionPoint: toDxfPoint(entity.insertionPoint),
      nominalHeight: entity.nominalHeight,
      contents: entity.contents
    };
    if (entity.referenceWidth) geom.referenceWidth = entity.referenceWidth;
    if (entity.attachmentPoint) geom.attachmentPoint = entity.attachmentPoint;
    if (entity.drawingDirection) geom.drawingDirection = entity.drawingDirection;
    if (entity.styleName) geom.styleName = entity.styleName;
    if (entity.rotation) geom.rotation = entity.rotation;
    if (entity.lineSpacingStyle) geom.lineSpacingStyle = entity.lineSpacingStyle;
    if (entity.lineSpacingFactor !== 1) geom.lineSpacingFactor = entity.lineSpacingFactor;
    // xAxisDirection – skip default (1,0,0)
    if (!(entity.xAxisDirection.x === 1 && entity.xAxisDirection.y === 0 && entity.xAxisDirection.z === 0)) {
      geom.xAxisDirection = toDxfPoint3D(entity.xAxisDirection);
    }
    if (entity.horizontalWidth) geom.horizontalWidth = entity.horizontalWidth;
    if (entity.verticalHeight) geom.verticalHeight = entity.verticalHeight;
    if (entity.backgroundFillSetting) geom.backgroundFillSetting = entity.backgroundFillSetting;
    if (entity.fillBoxScale !== 1.5) geom.fillBoxScale = entity.fillBoxScale;
    if (entity.backgroundFillColorACI) geom.backgroundFillColorACI = entity.backgroundFillColorACI;
    if (entity.backgroundFillColorRGB !== undefined) geom.backgroundFillColorRGB = entity.backgroundFillColorRGB;
    if (entity.backgroundTransparency) geom.backgroundTransparency = entity.backgroundTransparency;
    if (entity.columnType) geom.columnType = entity.columnType;
    if (entity.columnCount > 1) geom.columnCount = entity.columnCount;
    if (entity.columnFlowReversed) geom.columnFlowReversed = entity.columnFlowReversed;
    if (entity.columnAutoheight) geom.columnAutoheight = entity.columnAutoheight;
    if (entity.columnWidth) geom.columnWidth = entity.columnWidth;
    if (entity.columnGutter) geom.columnGutter = entity.columnGutter;
    if (entity.columnHeights.length > 0) geom.columnHeights = [...entity.columnHeights];
    return { ...base, type: "MTEXT", geometry: geom } satisfies DxfMTextEntity;
  }

  if (entity.type === "Dimension") {
    const geom: DxfDimensionEntity["geometry"] = {
      // SL-3 P3 — OR in the group-70 block-exclusive bit 32. Every DIMENSION we
      // export owns an anonymous `*D###` geometry block (assigned by the writer's
      // assignAnonymousDimensionBlockNames), so bit 32 ("the group-2 block
      // reference is referenced by this dimension only") is REQUIRED — Autodesk:
      // "value 32 is always set in R13 and later releases"; ezdxf sets
      // DIM_BLOCK_EXCLUSIVE on every dim it creates. ORing is idempotent, so an
      // imported dim (whose parsed dimensionType already carries bit 32, and any
      // ordinate bit 64 / user-location bit 128) round-trips byte-faithfully; the
      // low-3-bits subtype is untouched. This is done at the model→DXF boundary
      // (NOT the dxf-io writer, which stays a faithful verbatim serializer).
      dimensionType: entity.dimensionType | DIM_BLOCK_EXCLUSIVE,
      defPoint: toDxfPoint(entity.defPoint),
      textMidpoint: toDxfPoint(entity.textMidpoint)
    };
    if (entity.dimensionText) geom.dimensionText = entity.dimensionText;
    if (entity.styleName) geom.styleName = entity.styleName;
    if (entity.extLine1Point) geom.extLine1Point = toDxfPoint(entity.extLine1Point);
    if (entity.extLine2Point) geom.extLine2Point = toDxfPoint(entity.extLine2Point);
    if (entity.thirdPoint) geom.thirdPoint = toDxfPoint3D(entity.thirdPoint);
    if (entity.arcPoint) geom.arcPoint = toDxfPoint3D(entity.arcPoint);
    if (entity.linearAngle) geom.linearAngle = entity.linearAngle;
    if (entity.measurement) geom.measurement = entity.measurement;
    if (entity.leaderLength) geom.leaderLength = entity.leaderLength;
    if (entity.obliqueAngle) geom.obliqueAngle = entity.obliqueAngle;
    if (entity.textRotation) geom.textRotation = entity.textRotation;
    if (entity.textAngleRelativeToDimLine) geom.textAngleRelativeToDimLine = entity.textAngleRelativeToDimLine;
    if (entity.anonymousBlockName) geom.anonymousBlockName = entity.anonymousBlockName;
    // Group 71 — attachment point. Emit only when non-default (5 =
    // Middle-Center) so default dimensions stay byte-compact. The writer
    // re-applies the same default guard.
    if (entity.attachmentPoint !== 5) geom.attachmentPoint = entity.attachmentPoint;
    // S-DIMENSION-EXTRAS — version(280), lineSpacingStyle(72)/Factor(41),
    // clone insertion point (12/22/32). Carry onto the transport geometry
    // only when present / non-default; the dxf-io writer re-applies the same
    // guards. thickness(39) + extrusion(210/220/230) flow through the generic
    // COMMON channel above (`"thickness" in entity` / the extrusion narrow —
    // DIMENSION is NOT excluded from it), so they need no per-case wiring.
    if (entity.version !== undefined && entity.version !== 0) geom.version = entity.version;
    if (entity.lineSpacingStyle !== undefined) geom.lineSpacingStyle = entity.lineSpacingStyle;
    if (entity.lineSpacingFactor !== undefined && entity.lineSpacingFactor !== 1) {
      geom.lineSpacingFactor = entity.lineSpacingFactor;
    }
    if (entity.insertionPoint) geom.insertionPoint = toDxfPoint3D(entity.insertionPoint);
    // Opt 2b — encode per-entity DIMVAR overrides as ACAD/DSTYLE XData.
    // Merge with any pre-existing xdata on base (e.g. array-group XData)
    // so we don't clobber sibling app entries.
    const dimVarXData = buildDimensionStyleOverridesXData(entity.styleOverrides);
    // Issue #565 — MIRRTEXT-driven glyph handedness, encoded as this app's
    // OWN EDIT_DWG_DIM_OVERRIDES XDATA marker (see
    // `dimensionTextMirroredXData.ts`'s module doc). `undefined` for the
    // common false/absent case — no group invented, no clash with DSTYLE
    // (a separate app-id, appended alongside it below, never replacing it).
    const dimTextMirroredXData = buildDimensionTextMirroredXData(entity.textMirrored);
    const dimEntity: DxfDimensionEntity = {
      ...base,
      type: "DIMENSION",
      // SL-3 P1 — populate the per-subtype 2nd subclass marker(s) from the typed
      // `dimensionType` via the one-truth mapping (masks `& 7` internally). The
      // writer emits `100 AcDbDimension` then these verbatim, so an editor dim
      // gets its required subtype subclass (incl. the type-0 dual
      // AcDbAlignedDimension + AcDbRotatedDimension) — no reliance on the
      // writer's synthesis net (which stays inert because these markers arrive
      // present). The canonical [AcDbEntity, AcDbDimension] prefix is added by
      // the writer's mergeSubclassMarkers, so we carry only the subtype tail.
      subclassMarkers: dimensionSubtypeSubclassMarkers(entity.dimensionType),
      geometry: geom,
    };
    if (dimVarXData) {
      dimEntity.xdata = [...(dimEntity.xdata ?? []), dimVarXData];
    }
    if (dimTextMirroredXData) {
      dimEntity.xdata = [...(dimEntity.xdata ?? []), dimTextMirroredXData];
    }
    return dimEntity;
  }

  if (entity.type === "Block") {
    const geom: DxfInsertEntity["geometry"] = {
      blockName: entity.blockName,
      // Emit 3D insertion point (DXF group 30, z ?? 0) — DEFUSE S-INSERT-INSERTPT-Z.
      insertionPoint: toDxfPoint3D(entity.insertionPoint)
    };
    if (entity.scaleX !== 1) geom.scaleX = entity.scaleX;
    if (entity.scaleY !== 1) geom.scaleY = entity.scaleY;
    if (entity.scaleZ !== 1) geom.scaleZ = entity.scaleZ;
    if (entity.rotation) geom.rotation = entity.rotation;
    if (entity.columnCount !== 1) geom.columnCount = entity.columnCount;
    if (entity.rowCount !== 1) geom.rowCount = entity.rowCount;
    if (entity.columnSpacing) geom.columnSpacing = entity.columnSpacing;
    if (entity.rowSpacing) geom.rowSpacing = entity.rowSpacing;
    if (
      entity.extrusion &&
      !(entity.extrusion.x === 0 && entity.extrusion.y === 0 && entity.extrusion.z === 1)
    ) {
      geom.extrusion = toDxfPoint3D(entity.extrusion);
    }
    // Emit ATTRIB children when the INSERT carries attribute data. This
    // sets the group 66 flag (attributesFollow) and builds synthetic
    // DxfAttribEntity records so the writer can re-emit INSERT + ATTRIB*
    // + SEQEND in one sequence.
    if (entity.attributes.length > 0) {
      geom.attributesFollow = true;
      // round-6 finding 1 — track each tag's 0-based occurrence so a duplicate-tag
      // row resolves to its OWN ATTDEF anchor (occurrence 0 = unique-tag parity).
      // `baseCommon` (the space-derived common props) is built once and passed to
      // the sibling encoder so it needs nothing from this module (cycle-free).
      const attribBaseCommon = toDxfCommonProps(space);
      const tagOccurrence = new Map<string, number>();
      geom.attribs = entity.attributes.map((attr) => {
        const occurrenceIndex = tagOccurrence.get(attr.tag) ?? 0;
        tagOccurrence.set(attr.tag, occurrenceIndex + 1);
        return drawingAttribToDxfAttrib(attr, entity, attribBaseCommon, {
          forceLayerEmit: options?.forceLayerEmit === true,
          blockDefinition: options?.blocks?.[entity.blockName],
          occurrenceIndex,
        });
      });
      // T9b — the terminating SEQEND's own group-5 (imported verbatim or
      // skeleton-minted) so the writer emits `0 SEQEND / 5 <h> / 330 <owner>`
      // instead of a handle-less terminator ezdxf silently re-mints.
      if (entity.seqendHandle) geom.seqendHandle = entity.seqendHandle;
    }
    // Phase N.4 — emit per-INSERT representation data (overrides
    // linkage). When the BlockReference carries a non-trivial
    // representationOverrides record, mirror its fields onto the
    // DxfInsertEntity geometry so the writer can re-emit:
    //   * group 5 → 102 {ACAD_XDICTIONARY 360 <handle> 102 } via
    //     common.extensionDictionaryHandle (set below)
    //   * AcDbBlockRepETag XData (1071 etag) on entity.xdata
    //   * the dictionary chain itself (AcDbBlockRepresentation
    //     DICTIONARY + ACDB_BLOCKREPRESENTATION_DATA payload) is
    //     materialised by the OBJECTS-section writer once the bridge
    //     allocates handles for missing entries (Phase N.4 follow-up
    //     scope — not in this commit; the typed handle is preserved
    //     verbatim if the document already had it).
    const overrides = entity.representationOverrides;
    if (overrides) {
      const repData: NonNullable<DxfInsertEntity["geometry"]["representationData"]> = {
        parameterOverrides: { ...overrides.parameterOverrides },
      };
      if (overrides.currentVisibilityStateId !== undefined) {
        repData.currentVisibilityStateId = overrides.currentVisibilityStateId;
      }
      if (overrides.representationEtag !== undefined) {
        repData.etag = overrides.representationEtag;
      }
      if (overrides.representationDataHandle !== undefined) {
        repData.dataHandle = overrides.representationDataHandle;
      }
      geom.representationData = repData;
      // Mirror the handle into common props so the PRE_ENTITY_WRITERS
      // emit the 102 {ACAD_XDICTIONARY ... 102 } block before owner.
      if (overrides.representationDataHandle !== undefined) {
        common.extensionDictionaryHandle = overrides.representationDataHandle;
      }
    }
    const insertBase = base;
    const insertEntity: DxfInsertEntity = {
      ...insertBase,
      type: "INSERT",
      geometry: geom,
    };
    // Emit AcDbBlockRepETag XData when the etag is known. This is
    // additive — preserve any existing xdata (e.g. arrayXdata for
    // associative-array INSERTs).
    if (overrides?.representationEtag !== undefined) {
      const etagParts = overrides.representationEtag.split(":");
      const etagGroups = etagParts.map((part) => ({
        code: 1071,
        value: part,
      }));
      const xdataEntry = {
        appName: "AcDbBlockRepETag",
        groups: etagGroups,
      };
      insertEntity.xdata = [
        ...(insertEntity.xdata ?? []),
        xdataEntry,
      ];
    }
    return insertEntity;
  }

  if (entity.type === "Hatch") {
    const geom: DxfHatchEntity["geometry"] = {
      patternName: entity.patternName,
      solidFill: entity.solidFill,
      associative: entity.associative,
      boundaryPaths: entity.boundaryPaths.map((p: HatchBoundaryPath): DxfHatchBoundaryPath => ({
        pathType: p.pathTypeFlag,
        edges: p.edges.map(toDxfHatchEdge),
        // S-HATCH-ASSOC-REFS — per-path associative source-boundary handles.
        ...(p.sourceBoundaryHandles && p.sourceBoundaryHandles.length > 0
          ? { sourceBoundaryHandles: [...p.sourceBoundaryHandles] }
          : {})
      }))
    };
    // SL-7 (style-pattern-type-zero): project hatchStyle (75) / patternType (76)
    // by PRESENCE, not truthiness. Both are required schema fields (defaults 0 /
    // 1), so `entity.hatchStyle` is always a number — a truthiness guard
    // (`if (entity.hatchStyle)`) silently dropped the legitimate `75 0`
    // (odd-parity/nested style, the DEFAULT) and a user-defined `76 0`, which
    // then re-read as the reader default (1 = predefined) on reopen — a metadata
    // flip. Always assign; the dxf-io writer emits 75/76 unconditionally (ezdxf
    // `acdb_hatch` marks both non-optional).
    geom.hatchStyle = entity.hatchStyle;
    geom.patternType = entity.patternType;
    if (entity.patternAngle) geom.patternAngle = entity.patternAngle;
    if (entity.patternScale !== 1) geom.patternScale = entity.patternScale;
    // S-HATCH-CUSTOM — pattern double-line flag (group 77) + custom pattern-
    // definition lines (78 + 53/43/44/45/46/79/49). doubleLine is emitted only
    // when true (the dxf-io writer suppresses an absent 77; a false default
    // hatch stays byte-compact). customPatternLines are projected structurally
    // (offset {x,y} preserved, never flattened) and deep-copied.
    const hasCustomPatternLines =
      entity.customPatternLines !== undefined &&
      entity.customPatternLines.length > 0;
    if (entity.doubleLine) geom.doubleLine = true;
    if (hasCustomPatternLines) {
      // patternType (DXF group 76: 0=user-defined, 1=predefined, 2=custom) is
      // AUTHORITATIVE from the source — the parsed 76 on an imported hatch, or
      // the value the model creator set — and is projected verbatim above. We do
      // NOT infer custom from the mere presence of definition lines: predefined
      // hatches (e.g. ANSI31, 76=1) legitimately carry the 78/53/... block too,
      // so coercing on lines-present would corrupt their round-trip (Codex #246
      // round-2). Custom-hatch creators set patternType=2 explicitly.
      geom.customPatternLines = entity.customPatternLines!.map((line) => ({
        angle: line.angle,
        base: { x: line.base.x, y: line.base.y },
        offset: { x: line.offset.x, y: line.offset.y },
        dashLengths: [...line.dashLengths],
      }));
    }
    if (entity.elevation) geom.elevation = entity.elevation;
    // S-HATCH-ELEVATION-XY — leading 10/20 of the WCS elevation point.
    if (entity.elevationX !== undefined) geom.elevationX = entity.elevationX;
    if (entity.elevationY !== undefined) geom.elevationY = entity.elevationY;
    // S-HATCH-PIXELSIZE — DXF group 47.
    if (entity.pixelSize !== undefined) geom.pixelSize = entity.pixelSize;
    // S-HATCH-EXTRUSION — SINGLE-CHANNEL: project the typed model's OCS normal
    // onto `geom.extrusion` ONLY (the dxf-io HATCH writer case emits 210/220/230
    // from there; the COMMON group-writer skips HATCH). HATCH is excluded from
    // the generic common-extrusion narrow in toDxfCommonProps above, so this is
    // the sole export channel — and toDrawingEntity reads `g.extrusion` back,
    // closing the round-trip. The identity (0,0,1) normal is dropped (absent ⇒
    // default) to keep output compact.
    if (
      entity.extrusion &&
      !(
        entity.extrusion.x === 0 &&
        entity.extrusion.y === 0 &&
        entity.extrusion.z === 1
      )
    ) {
      geom.extrusion = toDxfPoint3D(entity.extrusion);
    }
    if (entity.seedPoints.length > 0) geom.seedPoints = entity.seedPoints.map(p => ({ x: p.x, y: p.y }));
    // Gradient fill (PDF-6.5a) — pass-through structural map. Only emitted
    // when the model carries a gradient; absent leaves the DXF entity's
    // `gradient` undefined so the writer skips the 450..470 block.
    if (entity.gradient) {
      geom.gradient = {
        kind: entity.gradient.kind,
        reserved: entity.gradient.reserved,
        oneColor: entity.gradient.oneColor,
        numberOfColors: entity.gradient.numberOfColors,
        rotation: entity.gradient.rotation,
        shift: entity.gradient.shift,
        tint: entity.gradient.tint,
        name: entity.gradient.name,
        colors: entity.gradient.colors.map(c => ({
          position: c.position,
          ...(c.aci !== undefined ? { aci: c.aci } : {}),
          ...(c.trueColor !== undefined ? { trueColor: c.trueColor } : {})
        }))
      };
    }
    return { ...base, type: "HATCH", geometry: geom } satisfies DxfHatchEntity;
  }

  if (entity.type === "Viewport") {
    const vpEntity = entity as ViewportEntity;
    const geom: DxfViewportEntity["geometry"] = {
      center: toDxfPoint(vpEntity.center),
      width: vpEntity.width,
      height: vpEntity.height
    };
    if (vpEntity.viewCenter) geom.viewCenter = { x: vpEntity.viewCenter.x, y: vpEntity.viewCenter.y };
    if (vpEntity.viewHeight) geom.viewHeight = vpEntity.viewHeight;
    if (vpEntity.viewDirection) geom.viewDirection = toDxfPoint3D(vpEntity.viewDirection);
    if (vpEntity.viewTarget) geom.viewTarget = toDxfPoint3D(vpEntity.viewTarget);
    if (vpEntity.viewTwistAngle) geom.viewTwistAngle = vpEntity.viewTwistAngle;
    if (vpEntity.lensLength !== 50) geom.lensLength = vpEntity.lensLength;
    // Batch-5 S-VIEWPORT-CLIPZ — front/back clip Z (DXF 43/44). The model
    // field + parser have existed since model.ts; the gap was here — toDxfEntity
    // never copied them onto the dxf geometry, so the writer's 43/44 emit had
    // no source and the values were dropped on export. Mirror the writer's
    // non-zero guard (writer skips 0; we only carry a non-zero value through).
    if (vpEntity.frontClipZ !== undefined && vpEntity.frontClipZ !== 0)
      geom.frontClipZ = vpEntity.frontClipZ;
    if (vpEntity.backClipZ !== undefined && vpEntity.backClipZ !== 0)
      geom.backClipZ = vpEntity.backClipZ;
    if (vpEntity.viewportStatus) geom.viewportStatus = vpEntity.viewportStatus;
    if (vpEntity.viewportId !== undefined) geom.viewportId = vpEntity.viewportId;
    if (vpEntity.displayLocked) geom.displayLocked = vpEntity.displayLocked;
    // P1 #2 fix: round-trip group-90 statusBitFlags with the lock bit (0x4000)
    // kept in sync with `displayLocked`. On import the entityParser derives
    // `displayLocked` FROM this bit; on export we must write group 90 back so
    // the derived value matches after round-trip.
    {
      // Start from the preserved original bitfield (if any), then set/clear
      // bit 0x4000 to match the current displayLocked state.
      const baseBits = vpEntity.statusBitFlags ?? 0;
      const lockBit = 0x4000;
      const syncedBits = vpEntity.displayLocked
        ? baseBits | lockBit
        : baseBits & ~lockBit;
      // Always emit group 90 so the lock state survives round-trip even when the
      // original DXF had no group-90 entry (new viewports created in-editor). The
      // group-90 non-rectangular-clip bit (0x10000) is masked out when no real 340
      // clip-boundary handle pairs with it (an authored polygon clip whose 340
      // synthesis is a deferred scope) — @codex #496 P1, maskOrphanNonRectClipBit.
      geom.statusBitFlags = maskOrphanNonRectClipBit(syncedBits, vpEntity);
    }
    // S-VIEWPORT-A — lighting block (DXF 141 brightness / 142 contrast).
    // Copy the typed model fields back so the writer emits 141/142 in the
    // AcDbViewport body (truth-corrected carrier: Autodesk DXF reference +
    // ezdxf acdb_viewport view_brightness=141 / view_contrast=142).
    //
    // `viewportCannoscaleName/Factor` are deliberately NOT emitted as any
    // entity-level group: the former PR-13 `142 ← factor` emit wrote the
    // annotation-scale factor into AutoCAD's view-contrast slot, and 144 is
    // not a VIEWPORT group code (the real carrier is the ext-dict XRECORD
    // ASDK_XREC_ANNOTATION_SCALE_INFO → 340 → AcDbScale, preserved verbatim
    // via the OBJECTS passthrough for imported files). Re-synthesising that
    // XRECORD for an in-session scale change is the documented deferred
    // follow-up (model.ts viewportEntitySchema comment) — until then we
    // emit NOTHING for it: never a phantom code, never a dangling handle
    // (ПРИОРИТЕТ #1).
    if (vpEntity.brightness !== undefined) geom.brightness = vpEntity.brightness;
    if (vpEntity.contrast !== undefined) geom.contrast = vpEntity.contrast;
    // Batch-5 S-VIEWPORT-SNAPGRID — copy typed snap/grid scalars back onto the
    // dxf geometry so the writer emits 13/23, 14/24, 15/25, 50, 61, 72.
    if (vpEntity.snapBase) geom.snapBase = { x: vpEntity.snapBase.x, y: vpEntity.snapBase.y };
    if (vpEntity.snapSpacing) geom.snapSpacing = { x: vpEntity.snapSpacing.x, y: vpEntity.snapSpacing.y };
    if (vpEntity.gridSpacing) geom.gridSpacing = { x: vpEntity.gridSpacing.x, y: vpEntity.gridSpacing.y };
    if (vpEntity.snapRotation !== undefined) geom.snapRotation = vpEntity.snapRotation;
    if (vpEntity.gridMajor !== undefined) geom.gridMajor = vpEntity.gridMajor;
    if (vpEntity.circleZoomPercent !== undefined) geom.circleZoomPercent = vpEntity.circleZoomPercent;
    // Batch-5 S-VIEWPORT-SHADEPLOT — plot-only shade/render mode (DXF 170/281).
    if (vpEntity.shadePlotMode !== undefined) geom.shadePlotMode = vpEntity.shadePlotMode;
    if (vpEntity.renderMode !== undefined) geom.renderMode = vpEntity.renderMode;
    // EXPORT-VP-FROZEN — emit the native per-VP frozen-layer handles (group 331).
    // PRESERVE-SOURCE, byte-order/casing faithful via `sourceFrozenLayerHandles ??
    // frozenLayerHandles` (import reconciliation clears RESOLVED entries from
    // `frozenLayerHandles` for the double-count-safe agent-bridge projector —
    // @codex #486 P2 — so the FULL verbatim list lives on `sourceFrozenLayerHandles`
    // instead, @codex #514 P2 "Preserve verbatim 331 order before clearing
    // handles"; falls back for a from-scratch viewport). A freshly frozen NAME with
    // no source slot is appended, resolved via the layer table; thaw is represented
    // POSITIVELY upstream (VPLAYER removes the handle), never inferred here.
    const layerTable = options?.layers ?? [];
    const derivedFrozenLayerHandles = resolveFrozenLayerHandlesFromNames(
      vpEntity.frozenLayers,
      vpEntity.sourceFrozenLayerHandles ?? vpEntity.frozenLayerHandles,
      layerTable,
    );
    if (derivedFrozenLayerHandles.length > 0) {
      geom.frozenLayerHandles = derivedFrozenLayerHandles;
    }
    // Effective XDATA names = semantic names UNIONED with any resolvable
    // native-331 name. `trustNativeAsAuthoritative` OMITTED — `frozenLayerHandles`
    // is a stale carrier that hasn't absorbed a fresh VPLAYER freeze yet (@codex
    // #514 "Keep new semantic freezes when writing XDATA").
    const effectiveFrozenNames = mergeFrozenLayerNamesFromHandles(
      vpEntity.frozenLayers,
      vpEntity.frozenLayerHandles,
      layerTable,
    );
    if (vpEntity.layerOverrideHandles && vpEntity.layerOverrideHandles.length > 0) {
      geom.layerOverrideHandles = vpEntity.layerOverrideHandles.map((r) => ({
        code: r.code,
        handle: r.handle,
      }));
    }
    // S-VIEWPORT-CLIP-HANDLE — emit the native clipping-boundary handle (340)
    // symmetrically so it survives export (the writer re-emits 340 from this
    // typed geometry field). Set only when present (preserve-source absence).
    //
    // group-90 non-rectangular-clip flag (0x10000): the source viewport's group
    // 90 (including the 0x10000 bit) is preserved verbatim via `statusBitFlags`
    // for an IMPORTED clipped viewport (real 340 present → the statusBitFlags
    // block above keeps the bit). The VPCLIP-authoring path
    // (`applyViewportClipSetPolygon`) now DOES set the 0x10000 bit + a typed
    // `clipBoundaryPaperSpace` WITHOUT a 340 handle (the SPATIAL_FILTER + 340
    // synthesis on write is the deferred EXPORT-VP-FROZEN scope, #486). To keep
    // export VALID until then, the statusBitFlags block above MASKS the 0x10000
    // bit whenever no real 340 handle is emitted (@codex #496 P1) — so an
    // authored-but-not-yet-exportable clip never writes an orphan non-rect flag.
    // Emit the 340 handle itself only when present (preserve-source absence).
    if (vpEntity.clippingBoundaryHandle !== undefined) {
      geom.clippingBoundaryHandle = vpEntity.clippingBoundaryHandle;
    }
    // iter-6: use stored vpOverrideScale/vpOverrideUcsName (set on import from
    // XDATA) so the authoritative rational scale and UCS name survive re-export.
    // Falls back to deriving scale from viewHeight/height for entities that have
    // never been through an XDATA import (e.g. newly created viewports).
    const vpXdataHolder: { xdata?: { appName: string; groups: { code: number; value: string }[] }[] } = {};
    writeViewportOverrides(
      {
        frozenLayers: effectiveFrozenNames,
        locked: vpEntity.displayLocked,
        scale: vpEntity.vpOverrideScale ?? scaleFromViewHeightRatio(vpEntity.viewHeight, vpEntity.height),
        ucsName: vpEntity.vpOverrideUcsName,
      },
      vpXdataHolder,
    );
    // Merge: base xdata (arrayXdata, if any) + VP overrides xdata
    const vpXdata = vpXdataHolder.xdata ?? [];
    const mergedXdata = [
      ...((base as { xdata?: DxfXDataEntry[] }).xdata ?? []),
      ...vpXdata,
    ];
    const vpBase = mergedXdata.length > 0 ? { ...base, xdata: mergedXdata } : base;
    return { ...vpBase, type: "VIEWPORT", geometry: geom } satisfies DxfViewportEntity;
  }

  if (entity.type === "Multileader") {
    const geom: DxfMLeaderEntity["geometry"] = {
      contentType: entity.contentType,
      // Full leader hierarchy incl. the sub-slice-A per-LEADER / per-LEADER_LINE
      // typed fields + break indices (sub-slice D — projected through the bridge).
      leaderRoots: modelMLeaderLeaderRootsToDxf(entity.leaderRoots),
    };
    // S-MLEADER-CONTEXT-STRUCTURAL (sub-slice D) — project the typed CONTEXT_DATA
    // record so C's faithful writer re-emits the rich header / INLINE MTextData /
    // BlockData / plane / per-leader metadata (instead of the lossy fallback
    // synthesis). When absent, the writer's fallback path runs — unchanged. The
    // verbatim in-context remainder rides `rawContextGroups`.
    if (entity.contextData) {
      geom.contextData = modelMLeaderContextToDxf(entity.contextData, {
        textContents: entity.textContents,
        textLocation: entity.textLocation,
        textRotation: entity.textRotation,
        textMirrored: entity.textMirrored ?? false,
        textHeight: entity.textHeight,
        landingGap: entity.landingGap,
        // SL-5 P2-4 — the flat line-spacing is the model's single source of
        // truth (renderer reads it, UI edits it); project it into the context
        // MTextData 45/170 on export so an edit / legacy-flat value reaches the
        // faithful writer instead of the stale source groups.
        textLineSpacingStyle: entity.textLineSpacingStyle,
        textLineSpacingFactor: entity.textLineSpacingFactor,
        // GitHub Codex review PR #558 round 39 (formal bot review, HEAD
        // 6bf071e6) P2 — `modelMLeaderContextToDxf`'s materialization gate
        // needs the ENTITY's own active content type (DXF group 172), not
        // cached CONTEXT_DATA block/MText presence, to decide whether
        // materializing a minimal MTextData is legitimate — see that
        // function's own `edits.contentType` doc.
        contentType: entity.contentType,
      });
    } else {
      // SL-5 P1 — an AUTHORED MLEADER (no parsed context) gets a COMPLETE
      // synthesized CONTEXT_DATA header so the writer emits a conformant record
      // (the marker-less fallback omitted the mandatory header). Always present
      // now → the writer's faithful `if (ctx)` path runs for every model MLEADER.
      geom.contextData = buildAuthoredMLeaderContext(entity);
    }
    if (entity.rawContextGroups && entity.rawContextGroups.length > 0) {
      geom.rawContextGroups = entity.rawContextGroups.map((grp) => ({
        code: grp.code,
        value: grp.value,
      }));
    }
    // #294 Codex P2 — re-emit the annotative SECONDARY CONTEXT_DATA blocks the
    // import projected (verbatim, per-scale; see modelMLeaderSecondaryContexts
    // ToDxf). The writer (buildAllMLeaderContextGroups) appends one block per
    // entry after the primary, each carrying its OWN raw remainder (#285-9).
    if (entity.secondaryContexts && entity.secondaryContexts.length > 0) {
      geom.secondaryContexts = modelMLeaderSecondaryContextsToDxf(
        entity.secondaryContexts,
      );
    }
    if (entity.textContents) geom.textContents = entity.textContents;
    // Carry the resolved MTEXT text-style NAME onto the transport. The
    // document-level export orchestrator resolves it to the STYLE table
    // handle and stamps `textStyleHandle` so the writer can re-emit group
    // 340 (no fabricated handle — the handle is the real STYLE entry's).
    // Emit only for a non-default style to keep "Standard" clouds compact
    // and consistent with the MLEADERSTYLE name-by-default convention.
    if (entity.textStyleName && entity.textStyleName !== "Standard") {
      geom.textStyleName = entity.textStyleName;
    }
    if (entity.textHeight) geom.textHeight = entity.textHeight;
    // textRotation is NOT a body field — its real ezdxf home is MTextData group
    // 42 (carried via geom.contextData.mTextData.rotation by
    // modelMLeaderContextToDxf above; AcDbMLeader body group 42 is
    // arrow_head_size). The faithful writer emits it from there; emitting a body
    // 42 here would collide with arrow_head_size (sub-slice C note).
    if (entity.arrowheadSize) geom.arrowheadSize = entity.arrowheadSize;
    if (entity.arrowheadBlock) geom.arrowheadBlock = entity.arrowheadBlock;
    if (entity.textLocation) geom.textLocation = toDxfPoint(entity.textLocation);
    if (entity.styleName) geom.styleName = entity.styleName;
    // SL-5 P3a — carry the AcDbMLeader version (270). Absent on the model → the
    // writer defaults to 2 (ezdxf's unconditional write). SL-5 P2 — carry a
    // source-preserved leader-style handle (340); an authored MLEADER with no
    // handle yet is resolved from styleName by stampMLeaderLeaderStyleHandles at
    // document-export time (the handle is stamped onto geom.leaderStyleHandle).
    if (entity.version !== undefined) geom.version = entity.version;
    if (entity.leaderStyleHandle) geom.leaderStyleHandle = entity.leaderStyleHandle;
    if (entity.landingEnabled !== undefined) geom.landingEnabled = entity.landingEnabled;
    if (entity.doglegEnabled !== undefined) geom.doglegEnabled = entity.doglegEnabled;
    if (entity.doglegLength) geom.doglegLength = entity.doglegLength;
    // landingGap → CONTEXT_DATA 145 only (gap-C.2 removed body 44). Project to
    // the transport ONLY when non-default (≠2) so the writer's FALLBACK path
    // (marker-less / no contextData) emits 145 just for a meaningful landing gap
    // (Codex #271); a default-2 MLEADER stays compact. A context-bearing MLEADER
    // takes the FAITHFUL path, which emits 145 from contextData.landingGapSize —
    // an edit to that gap is projected onto landingGapSize inside
    // modelMLeaderContextToDxf above (Codex #285-7), so this body field only
    // drives the fallback path (harmless when also set for a context entity).
    if (entity.landingGap !== undefined && entity.landingGap !== 2) {
      geom.landingGap = entity.landingGap;
    }
    if (entity.textAttachmentType) geom.textAttachmentType = entity.textAttachmentType;
    if (entity.textAttachmentDirection) geom.textAttachmentDirection = entity.textAttachmentDirection;
    if (entity.textFrameDisplay) geom.textFrameDisplay = entity.textFrameDisplay;
    if (entity.textLineSpacingStyle) geom.textLineSpacingStyle = entity.textLineSpacingStyle;
    if (entity.textLineSpacingFactor !== 1) geom.textLineSpacingFactor = entity.textLineSpacingFactor;
    if (entity.leaderLineType) geom.leaderLineType = entity.leaderLineType;
    if (entity.leaderLineColor !== "ByBlock") geom.leaderLineColor = entity.leaderLineColor;
    // #258 F3 — encode the model lineweight STRING ("0.25"/"ByLayer"/raw enum)
    // as the DXF group-171 enum INTEGER before placing it on the transport. The
    // dxf-io writer emits group 171 verbatim, so a raw "0.25" would produce an
    // invalid `171\n0.25` (and the int reader would parse it to 0). ByBlock maps
    // to null → omit the field (compact default; writer skips 171, parser
    // re-defaults to ByBlock). Mapping lives at the bridge (dxf-io is upstream).
    {
      const code171 = modelLeaderLineWeightToDxf171(entity.leaderLineWeight);
      if (code171 !== null) geom.leaderLineWeight = String(code171);
    }
    if (entity.blockName) geom.blockName = entity.blockName;
    // Re-emit the raw body-344 BLOCK_RECORD handle the parser preserved so the
    // writer round-trips 344 verbatim (the resolved blockName drives render/UI
    // but is NOT a valid 344 handle) (Codex #271).
    if (entity.blockRecordHandle) geom.blockRecordHandle = entity.blockRecordHandle;
    if (entity.blockScale !== 1) geom.blockScale = entity.blockScale;
    if (entity.blockRotation !== undefined) geom.blockRotation = entity.blockRotation;
    // #432 r11 (@codex seam) — project the body block_scale_vector (10/20/30)
    // model → DXF geometry so the writer re-emits it on the app save path (the
    // pure dxf-io round-trip already preserved it; this closes the DrawingModel
    // seam). Source-preserving: absent on the model → absent on export.
    if (entity.blockScaleVector) {
      geom.blockScaleVector = {
        x: entity.blockScaleVector.x,
        y: entity.blockScaleVector.y,
        z: entity.blockScaleVector.z,
      };
    }
    // #432 r11 (@codex seam) — project the block-attribute linkage tuples
    // (330/177/44/302) model → DXF geometry so the writer re-emits them on the app
    // save path. Without this an imported block-content MLEADER saved via
    // import→export dropped every ATTDEF tuple (block attributes reopened as
    // defaults). The import direction (dxfBridge.drawingToDxf) already populates
    // the model field; this is the symmetric export arm.
    if (entity.blockAttributeReferences.length > 0) {
      geom.blockAttributeReferences = entity.blockAttributeReferences.map((ref) => ({
        attDefHandle: ref.attDefHandle,
        index: ref.index,
        width: ref.width,
        value: ref.value,
      }));
    }
    // AcDbMLeader BODY group 176 (block_connection_type) — preserve verbatim
    // through the bridge, INCLUDING source absence. The model field is now
    // `.optional()` (Codex #270 regression fix), so this `!== undefined` guard
    // is load-bearing: a source that omitted body 176 stays omitted (the writer
    // skips 176), and a source with 176=N round-trips N. Previously the field
    // defaulted to 0 so this guard never fired → an invented `176\n0` re-emitted
    // on every round-trip of a source-omitted-176 MLEADER.
    if (entity.blockConnectionType !== undefined) {
      geom.blockConnectionType = entity.blockConnectionType;
    }
    // Body 90/175/43 are z.number().default(0) — always defined on the model
    // output type, so project with `!== undefined` (never truthiness): the value
    // 0 is valid and MUST survive. Truthiness here silently dropped body 90=0 /
    // 175=0 / 43=0.0, matching neither the dxf-io writer's `!== undefined` emit
    // nor the import's `?? 0` (gap-C.2 S1 round-trip 0-value corruption fix).
    if (entity.propertyOverrideFlags !== undefined) geom.propertyOverrideFlags = entity.propertyOverrideFlags;
    // gap-C.2 — AcDbMLeader BODY group 175 (text_alignment_type). Project the
    // typed model field onto the transport; the dxf-io writer emits body 175.
    if (entity.textAlignmentType !== undefined) geom.textAlignmentType = entity.textAlignmentType;
    return { ...base, type: "MLEADER", geometry: geom } satisfies DxfMLeaderEntity;
  }

  if (entity.type === "Revision Cloud") {
    // Set the TYPED geometry fields (revisionCloudStyle / creationMode /
    // approximateArcLength) only. The dxf-io writer is the SINGLE encode
    // point for the "RevcloudProps" XData appid — it projects these typed
    // fields into XData on serialize (skipping all-default clouds for a
    // compact round-trip). The bridge must NOT also pre-encode that XData
    // here or it would double-emit a RevcloudProps block.
    const revGeom: DxfRevcloudEntity["geometry"] = {
      vertices: entity.vertices.map(toDxfVertex),
      closed: entity.closed,
      vertexCount: entity.vertices.length,
      arcLength: entity.arcChordLength,
    };
    // DXF group 70 bit 128 — plinegen (S-LWPOLYLINE parity). Only set when true
    // so the writer emits a compact 70 for a plain (non-plinegen) cloud.
    if (entity.plinegen) revGeom.plinegen = true;
    // S-REVCLOUD-FIELDS — LWPOLYLINE-derived scalars on the geometry. Emit
    // only non-default values so the writer output stays byte-compact (the
    // dxf-io writer omits 43/38 when 0). thickness (39) + extrusion
    // (210/220/230) route through the COMMON channel automatically (REVCLOUD
    // is not excluded from the common thickness/extrusion narrows above).
    if (entity.constantWidth !== undefined && entity.constantWidth !== 0) {
      revGeom.constantWidth = entity.constantWidth;
    }
    if (entity.elevation !== undefined && entity.elevation !== 0) {
      revGeom.elevation = entity.elevation;
    }
    if (entity.revisionCloudStyle !== undefined) {
      revGeom.revisionCloudStyle = entity.revisionCloudStyle;
    }
    if (entity.creationMode !== undefined) {
      revGeom.creationMode = entity.creationMode;
    }
    if (entity.approximateArcLength !== undefined) {
      revGeom.approximateArcLength = entity.approximateArcLength;
    }
    const revEntity: DxfRevcloudEntity = {
      ...base,
      type: "REVCLOUD",
      geometry: revGeom,
    };
    return revEntity satisfies DxfRevcloudEntity;
  }

  if (entity.type === "Wipeout") {
    const wipeoutGeom: DxfWipeoutEntity["geometry"] = {
      vertices: entity.vertices.map((v) => ({ x: v.x, y: v.y })),
      frame: entity.frame,
      clipMode: entity.clipMode
    };
    return { ...base, type: "WIPEOUT", geometry: wipeoutGeom } satisfies DxfWipeoutEntity;
  }

  if (entity.type === "Polyline") {
    // Legacy heavy POLYLINE. Each VERTEX record is re-materialised as a full
    // DXF entity whose common props are DERIVED from this parent — layer,
    // linetype, lineweight, colour and space are not independently editable on
    // a VERTEX in AutoCAD (ezdxf `Polyline.on_layer_change` /
    // `on_linetype_change` rewrite every child when the parent changes), so
    // deriving them is the faithful model rather than a shortcut. Everything
    // the VERTEX owns — its handle, its geometry AND its four passthrough
    // channels (xdata / ext-dict / extraGroups / subclassMarkers) — comes back
    // out of the typed record; see `polylineVertexRecordSchema`.
    const vertices: DxfVertexEntity[] = entity.vertices.map((record) => {
      const vertexCommon: DxfCommonEntityProps = { ...base.common };
      // Drop the parent's identity — a vertex has its own handle, its own
      // extension dictionary, and its owner is the POLYLINE (filled in by the
      // writer when this is left unset).
      delete vertexCommon.handle;
      delete vertexCommon.ownerHandle;
      delete vertexCommon.extensionDictionaryHandle;
      if (record.handle) vertexCommon.handle = record.handle;
      // The VERTEX's OWN `102 {ACAD_XDICTIONARY 360 <h> 102 }` — restored from
      // the record, never inherited from the parent (deleted just above).
      if (record.extensionDictionaryHandle !== undefined) {
        vertexCommon.extensionDictionaryHandle = record.extensionDictionaryHandle;
      }
      const vertexGeom: DxfVertexEntity["geometry"] = {
        location: toDxfPoint3D(record.location),
      };
      if (record.startWidth !== undefined) vertexGeom.startWidth = record.startWidth;
      if (record.endWidth !== undefined) vertexGeom.endWidth = record.endWidth;
      if (record.bulge !== undefined) vertexGeom.bulge = record.bulge;
      if (record.flags !== undefined) vertexGeom.flags = record.flags;
      if (record.curveFitTangent !== undefined) {
        vertexGeom.curveFitTangent = record.curveFitTangent;
      }
      if (record.faceVertexIndices) {
        vertexGeom.faceVertexIndices = [...record.faceVertexIndices];
      }
      if (record.vertexId !== undefined) vertexGeom.vertexId = record.vertexId;
      // The three remaining per-vertex passthrough channels, restored verbatim.
      // `subclassMarkers` re-enters `mergeSubclassMarkers`, which drops the
      // group-70-derived family markers and keeps only a genuine extension;
      // `extraGroups` carries the `102 {ACAD_REACTORS … 102 }` block and every
      // unmapped group; `xdata` carries the `1001` blocks. Emitting `[]` here
      // — as this branch used to — DELETED all three on the first
      // open-and-save, records the pre-typed raw passthrough preserved.
      return {
        kind: "supported-entity",
        type: "VERTEX",
        common: vertexCommon,
        subclassMarkers: record.subclassMarkers ? [...record.subclassMarkers] : [],
        extraGroups: record.extraGroups
          ? record.extraGroups.map((group) => ({
              code: group.code,
              value: group.value,
            }))
          : [],
        ...(record.xdata
          ? {
              xdata: record.xdata.map((entry) => ({
                appName: entry.appName,
                groups: entry.groups.map((group) => ({
                  code: group.code,
                  value: group.value,
                })),
              })),
            }
          : {}),
        geometry: vertexGeom,
      } satisfies DxfVertexEntity;
    });
    const polyGeom: DxfPolylineEntity["geometry"] = {
      elevation: toDxfPoint3D(entity.elevation),
      flags: entity.flags,
      vertices,
    };
    if (entity.verticesFollow !== undefined) {
      polyGeom.verticesFollow = entity.verticesFollow;
    }
    if (entity.defaultStartWidth !== undefined) {
      polyGeom.defaultStartWidth = entity.defaultStartWidth;
    }
    if (entity.defaultEndWidth !== undefined) {
      polyGeom.defaultEndWidth = entity.defaultEndWidth;
    }
    if (entity.meshMCount !== undefined) polyGeom.meshMCount = entity.meshMCount;
    if (entity.meshNCount !== undefined) polyGeom.meshNCount = entity.meshNCount;
    if (entity.smoothSurfaceMDensity !== undefined) {
      polyGeom.smoothSurfaceMDensity = entity.smoothSurfaceMDensity;
    }
    if (entity.smoothSurfaceNDensity !== undefined) {
      polyGeom.smoothSurfaceNDensity = entity.smoothSurfaceNDensity;
    }
    if (entity.surfaceType !== undefined) polyGeom.surfaceType = entity.surfaceType;
    if (entity.seqendHandle || entity.seqendMetadata) {
      // Rebuild the terminating SEQEND record so its own handle survives the
      // round-trip (the loss `dxfBridge.seqendPassthrough.ts` used to work
      // around) — AND, with it, everything else the imported terminator
      // carried: its own layer / linetype / colour, its
      // `102 {ACAD_XDICTIONARY`, its `102 {ACAD_REACTORS` block, every group
      // the SEQEND reader did not map, a non-canonical `100` subclass
      // extension and its `1001` XDATA blocks. Keeping only the handle here
      // (and emitting `subclassMarkers: []` / `extraGroups: []`) DELETED all
      // of that on the first open-and-save, although dxf-io parsed and
      // retained the whole record (PR #785 @codex round 2, F1) — the same
      // class of loss as the per-VERTEX one fixed in round 1, one level up.
      //
      // The carried half is SPREAD, never re-listed field by field, so a
      // channel added to the record later re-emits automatically; the derived
      // half is stamped after it and therefore always wins: the own handle
      // (`seqendHandle` — the one truth the remint machinery updates), the
      // owner (the POLYLINE owns its terminator, ezdxf `Polyline.new_seqend`)
      // and the container space / layout the record is serialized inside.
      const { common: carriedCommon, ...carriedChannels } =
        entity.seqendMetadata ?? { common: {} };
      const seqendCommon: DxfCommonEntityProps = { ...carriedCommon };
      if (entity.seqendHandle) seqendCommon.handle = entity.seqendHandle;
      // A SEQEND that carried no group 8 of its own (or a polyline built by a
      // command, which has no imported terminator at all) inherits the
      // parent's layer, exactly as before — never overriding an authored one.
      if (seqendCommon.layer === undefined && base.common.layer !== undefined) {
        seqendCommon.layer = base.common.layer;
      }
      if (base.common.space !== undefined) seqendCommon.space = base.common.space;
      if (base.common.layoutName) seqendCommon.layoutName = base.common.layoutName;
      if (base.common.handle) seqendCommon.ownerHandle = base.common.handle;
      polyGeom.seqend = {
        kind: "supported-entity",
        type: "SEQEND",
        subclassMarkers: [],
        extraGroups: [],
        ...carriedChannels,
        common: seqendCommon,
        geometry: {},
      };
    }
    return { ...base, type: "POLYLINE", geometry: polyGeom } satisfies DxfPolylineEntity;
  }

  if (entity.type === "Table") {
    const tableGeom: DxfAcadTableEntity["geometry"] = {
      blockName: entity.blockName,
      insertionPoint: toDxfPoint3D(entity.insertionPoint),
      rowHeights: [...entity.rowHeights],
      columnWidths: [...entity.columnWidths],
      contentGroups: entity.contentGroups.map((group) => ({
        code: group.code,
        value: group.value,
      })),
      cachedGraphicsChunks: [...entity.cachedGraphicsChunks],
    };
    if (entity.horizontalDirection) {
      tableGeom.horizontalDirection = toDxfPoint3D(entity.horizontalDirection);
    }
    if (entity.version !== undefined) tableGeom.version = entity.version;
    if (entity.tableStyleHandle !== undefined) {
      tableGeom.tableStyleHandle = entity.tableStyleHandle;
    }
    if (entity.blockRecordHandle !== undefined) {
      tableGeom.blockRecordHandle = entity.blockRecordHandle;
    }
    if (entity.valueFlag !== undefined) tableGeom.valueFlag = entity.valueFlag;
    if (entity.rowCount !== undefined) tableGeom.rowCount = entity.rowCount;
    if (entity.columnCount !== undefined) tableGeom.columnCount = entity.columnCount;
    if (entity.overrideFlag !== undefined) {
      tableGeom.overrideFlag = entity.overrideFlag;
    }
    if (entity.borderColorOverrideFlag !== undefined) {
      tableGeom.borderColorOverrideFlag = entity.borderColorOverrideFlag;
    }
    if (entity.borderLineweightOverrideFlag !== undefined) {
      tableGeom.borderLineweightOverrideFlag = entity.borderLineweightOverrideFlag;
    }
    if (entity.borderVisibilityOverrideFlag !== undefined) {
      tableGeom.borderVisibilityOverrideFlag = entity.borderVisibilityOverrideFlag;
    }
    if (entity.cachedGraphicsByteCount !== undefined) {
      tableGeom.cachedGraphicsByteCount = entity.cachedGraphicsByteCount;
    }
    return { ...base, type: "ACAD_TABLE", geometry: tableGeom } satisfies DxfAcadTableEntity;
  }

  if (entity.type === "OLE Frame") {
    const oleGeom: DxfOle2FrameEntity["geometry"] = {
      upperLeft: toDxfPoint3D(entity.upperLeft),
      lowerRight: toDxfPoint3D(entity.lowerRight),
      binaryChunks: [...entity.binaryChunks],
    };
    if (entity.oleVersion !== undefined) oleGeom.oleVersion = entity.oleVersion;
    if (entity.objectDescription !== undefined) {
      oleGeom.objectDescription = entity.objectDescription;
    }
    if (entity.oleObjectType !== undefined) {
      oleGeom.oleObjectType = entity.oleObjectType;
    }
    if (entity.tileModeDescriptor !== undefined) {
      oleGeom.tileModeDescriptor = entity.tileModeDescriptor;
    }
    if (entity.drawAspect !== undefined) oleGeom.drawAspect = entity.drawAspect;
    if (entity.binaryByteCount !== undefined) {
      oleGeom.binaryByteCount = entity.binaryByteCount;
    }
    if (entity.terminator !== undefined) oleGeom.terminator = entity.terminator;
    return { ...base, type: "OLE2FRAME", geometry: oleGeom } satisfies DxfOle2FrameEntity;
  }

  // Rectangular Associative Array — SL-18: a NON-associative grid whose source
  // block is DEFINED in the document serializes as a genuine MINSERT (`0 INSERT`
  // + AcDbMInsertBlock) so AutoCAD reconstructs the grid on open; an ASSOCIATIVE
  // array (ACDBASSOCARRAY graph present) — or one whose source block is absent
  // (would dangle) — is PRESERVED as a plain block reference, byte-identical to
  // before. The decision + geometry builder live in dxfBridge.arrayMinsert.ts
  // (associativity + block existence are known HERE, at the typed model → DXF
  // boundary; the resulting entity TYPE encodes the decision for the raw
  // round-trip). `options.blocks` is the document block table (threaded by the
  // export path); its absence (some block-child callers) safely disables the
  // upgrade. `resolveSourceBlockName` resolves the block CASE-INSENSITIVELY
  // (AutoCAD folds block names) and returns the CANONICAL stored key so the
  // MINSERT emits the block's real spelling (not a mismatched source case) —
  // see its doc in dxfBridge.arrayMinsert.
  const resolvedSourceBlockName = resolveSourceBlockName(
    options?.blocks,
    entity.sourceBlockName,
  );
  return buildArrayRectDxfEntity(entity, base, resolvedSourceBlockName);
}
